/**
 * Ed25519 signing and verification over exact bytes, via the WebCrypto
 * `crypto.subtle` API that Node and Cloudflare Workers both implement — no
 * `node:crypto`, no `node:*` import, no third-party signing library. The
 * same `verifySignature` call this module exports is what `publish.ts` uses
 * to check a fetched manifest before trusting its sequence, and what the
 * Worker uses to check a manifest before serving a redirect from it.
 *
 * Every function signs or verifies a `Uint8Array` — never a JavaScript
 * object re-serialized on the spot. `encodeManifest` in `manifest.ts`
 * produces the bytes; this module never re-derives them.
 */
import { z } from 'zod';

const ED25519_ALGORITHM = { name: 'Ed25519' } as const;

/** The JWK shape an Ed25519 private key exports to and imports from. */
const ed25519PrivateJwkSchema = z.object({
  kty: z.literal('OKP'),
  crv: z.literal('Ed25519'),
  d: z.string().min(1),
  x: z.string().min(1),
});

export type Ed25519PrivateJwk = z.infer<typeof ed25519PrivateJwkSchema>;

// `btoa`/`atob` — not `Buffer` — because this module must import cleanly in
// a Cloudflare Worker, which has no `Buffer` global without the
// `nodejs_compat` flag. Both functions operate on "binary strings" (one
// code unit per byte), so a `Uint8Array` has to pass through
// `String.fromCharCode` / `charCodeAt` on the way through.
const toBase64 = (bytes: Uint8Array): string => btoa(String.fromCharCode(...bytes));
const fromBase64 = (base64: string): Uint8Array =>
  new Uint8Array(
    atob(base64)
      .split('')
      .map((char) => char.charCodeAt(0)),
  );

/**
 * Imports an Ed25519 private key from the JWK JSON string `generate-key.ts`
 * stores in 1Password and the `RELEASE_SIGNING_KEY` secret carries.
 * @throws if the JSON does not parse, or does not carry an Ed25519 OKP JWK
 */
export const importSigningKey = async (privateKeyJwkJson: string): Promise<CryptoKey> => {
  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(privateKeyJwkJson);
  } catch {
    throw new Error('RELEASE_SIGNING_KEY is not valid JSON');
  }
  const jwk = ed25519PrivateJwkSchema.parse(parsedJson);
  return crypto.subtle.importKey('jwk', jwk, ED25519_ALGORITHM, false, ['sign']);
};

/**
 * Imports an Ed25519 public key from its raw 32-byte form, base64-encoded —
 * the form `generate-key.ts` prints and `public-keys.ts` stores.
 */
export const importVerificationKey = async (publicKeyBase64: string): Promise<CryptoKey> =>
  crypto.subtle.importKey('raw', fromBase64(publicKeyBase64), ED25519_ALGORITHM, true, ['verify']);

/** Signs `bytes` and returns the signature, base64-encoded. */
export const signBytes = async (privateKey: CryptoKey, bytes: Uint8Array): Promise<string> => {
  const signature = await crypto.subtle.sign(ED25519_ALGORITHM, privateKey, bytes);
  return toBase64(new Uint8Array(signature));
};

/**
 * Verifies a base64 signature against `bytes`. Never throws on a bad
 * signature — a mismatched key, a tampered byte, or malformed base64 all
 * resolve to `false`, so callers can treat every failure mode the same way:
 * refuse to trust the bytes.
 */
export const verifyBytes = async (
  publicKey: CryptoKey,
  signatureBase64: string,
  bytes: Uint8Array,
): Promise<boolean> => {
  try {
    return await crypto.subtle.verify(
      ED25519_ALGORITHM,
      publicKey,
      fromBase64(signatureBase64),
      bytes,
    );
  } catch {
    return false;
  }
};

/**
 * Verifies an Ed25519 signature over `manifestBytes`, using the public key
 * registered for `keyId`. The one "look up `keyId`, then verify" function
 * the publish pipeline, the redirect Worker, and the landing page share.
 *
 * @returns `false` for every failure mode (unknown keyId, malformed
 * base64, wrong-length key or signature, cryptographic mismatch) — callers
 * treat "not verified" as one outcome, never distinguishing why.
 */
export const verifyManifestSignature = async (input: {
  manifestBytes: ArrayBuffer;
  keyId: string;
  signatureBase64: string;
  publicKeysByKeyId: Record<string, string>;
}): Promise<boolean> => {
  const publicKeyBase64 = input.publicKeysByKeyId[input.keyId];
  if (publicKeyBase64 === undefined) {
    return false;
  }

  try {
    const publicKey = await importVerificationKey(publicKeyBase64);
    return await verifyBytes(publicKey, input.signatureBase64, new Uint8Array(input.manifestBytes));
  } catch {
    // A malformed base64 key/signature or an unsupported key shape lands
    // here — every one of them means "not a valid signature".
    return false;
  }
};

/**
 * Derives the short, stable identifier a public key is filed under —
 * `public-keys.ts`'s `Record` key, and the `keyId` field in every
 * `version.json.sig`. Deterministic from the public key alone, so rotating
 * keys never collides and re-deriving a keyId from a known key always
 * agrees with what was printed at generation time.
 * @param publicKeyRaw - the raw 32-byte Ed25519 public key
 * @returns the first 16 hex characters of sha256(publicKeyRaw)
 */
export const deriveKeyId = async (publicKeyRaw: Uint8Array): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', publicKeyRaw);
  const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
  return hex.slice(0, 16);
};

/** Base64-encodes a raw Ed25519 public key, e.g. before printing or filing it. */
export const encodePublicKey = (publicKeyRaw: Uint8Array): string => toBase64(publicKeyRaw);
